/**
 * RelationsPanel — Verknüpfungen section of the farm object view
 * (design §3.3): forward FK references resolved to display names,
 * reverse references with counts + sample chips, all navigable.
 * Relations are first-class (design principle 3) — this is the
 * Foundry-style traversal surface.
 */
import React from 'react';
import { ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import type { EntityRelations, ReverseRelation } from '../../../services/twinService';
import './RelationsPanel.css';

export interface RelationsPanelProps {
    relations?: EntityRelations;
    isLoading?: boolean;
    isError?: boolean;
    /** Navigate to another object view. */
    onNavigate: (entityType: string, entityId: string) => void;
    /** Navigate to a filtered list (reverse relation "alle anzeigen");
     * absent when the source type has no lens. */
    onShowAll?: (relation: ReverseRelation) => void;
    /** entity_type → lens exists (controls the "alle anzeigen" link). */
    hasLensForType?: (entityType: string) => boolean;
}

export const RelationsPanel: React.FC<RelationsPanelProps> = ({
    relations,
    isLoading,
    isError,
    onNavigate,
    onShowAll,
    hasLensForType,
}) => {
    if (isLoading) {
        return (
            <div className="relations-panel__loading">
                <Loader2 size={14} className="relations-panel__spinner" />
                Verknüpfungen werden geladen…
            </div>
        );
    }
    if (isError) {
        return <p className="relations-panel__none">Verknüpfungen konnten nicht geladen werden</p>;
    }
    const forward = relations?.forward ?? [];
    const reverse = relations?.reverse ?? [];
    if (forward.length === 0 && reverse.length === 0) {
        return <p className="relations-panel__none">Keine Verknüpfungen vorhanden</p>;
    }
    // The same source type can point here through several FK roles
    // (delivery_event.sender_id AND .receiver_id → this organization).
    // Identical bare labels read as duplicates — disambiguate with the
    // role label ("Lieferung · Absender" / "Lieferung · Empfänger").
    const labelCounts = new Map<string, number>();
    for (const rel of reverse) labelCounts.set(rel.label, (labelCounts.get(rel.label) ?? 0) + 1);
    const reverseLabel = (rel: ReverseRelation) =>
        (labelCounts.get(rel.label) ?? 0) > 1 && rel.via_label ? `${rel.label} · ${rel.via_label}` : rel.label;

    return (
        <div className="relations-panel">
            {forward.map((rel) => (
                <div key={`fwd-${rel.field}`} className="relations-panel__row">
                    <span className="relations-panel__label">
                        <ArrowRight size={12} className="relations-panel__dir" />
                        {rel.label}
                    </span>
                    <button
                        type="button"
                        className="relations-panel__link"
                        onClick={() => onNavigate(rel.target_type, rel.target_id)}
                    >
                        {rel.display_name}
                    </button>
                </div>
            ))}
            {reverse.map((rel) => (
                <div key={`rev-${rel.source_type}-${rel.via_field}`} className="relations-panel__row relations-panel__row--reverse">
                    <span className="relations-panel__label">
                        <ArrowLeft size={12} className="relations-panel__dir" />
                        {reverseLabel(rel)}
                        <span className="relations-panel__count">{rel.count}</span>
                    </span>
                    <span className="relations-panel__chips">
                        {rel.sample.map((s) => (
                            <button
                                key={s.entity_id}
                                type="button"
                                className="relations-panel__chip"
                                onClick={() => onNavigate(s.entity_type, s.entity_id)}
                            >
                                {s.display_name}
                            </button>
                        ))}
                        {rel.count > rel.sample.length && onShowAll && hasLensForType?.(rel.source_type) && (
                            <button
                                type="button"
                                className="relations-panel__show-all"
                                onClick={() => onShowAll(rel)}
                            >
                                alle {rel.count} anzeigen
                            </button>
                        )}
                    </span>
                </div>
            ))}
        </div>
    );
};

export default RelationsPanel;
